import { useState } from 'react';

import { CodePicker } from '../components/admin/CodePicker';
import { ResourceTab, type FormProps } from '../components/admin/ResourceTab';
import { SparkInput } from '../components/SparkInput';
import {
  WEEKDAYS,
  flightPath,
  formatDays,
  formatMinute,
  parseMinute,
  type Aircraft,
  type Airline,
  type Airport,
  type City,
  type Country,
  type FareRule,
  type FareRuleClass,
  type Flight,
  type Itinerary,
  type TaxRule,
} from '../lib/reference';

const TABS = [
  { id: 'fare-rules', label: 'Fare rules' },
  { id: 'taxes', label: 'Taxes' },
  { id: 'flights', label: 'Flights' },
  { id: 'itineraries', label: 'Itineraries' },
  { id: 'airlines', label: 'Airlines' },
  { id: 'airports', label: 'Airports' },
  { id: 'cities', label: 'Cities' },
  { id: 'countries', label: 'Countries' },
  { id: 'aircraft', label: 'Aircraft' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const up = (value: string) => value.toUpperCase();

interface PickerProps {
  label: string;
  value: string;
  onChange: (code: string) => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
}

function AirlinePicker(props: PickerProps) {
  return (
    <CodePicker<Airline> {...props} resource="airlines" maxLength={2}
      toOption={(a) => ({ code: a.code, label: a.name })} />
  );
}

function AirportPicker(props: PickerProps) {
  return (
    <CodePicker<Airport> {...props} resource="airports" maxLength={3}
      toOption={(a) => ({ code: a.code, label: `${a.city} — ${a.name}, ${a.country}` })} />
  );
}

function AircraftPicker(props: PickerProps) {
  return (
    <CodePicker<Aircraft> {...props} resource="aircraft" maxLength={3}
      toOption={(a) => ({ code: a.code, label: a.name })} />
  );
}

function CountryPicker(props: PickerProps) {
  return (
    <CodePicker<Country> {...props} resource="countries" maxLength={2}
      toOption={(c) => ({ code: c.code, label: c.name })} />
  );
}

/** Root and administrator maintenance of the data the host engine runs on. */
export function ReferencePage() {
  const [tab, setTab] = useState<TabId>(() => {
    const saved = safeRead('sabre.reference.tab');
    return TABS.some((t) => t.id === saved) ? (saved as TabId) : 'fare-rules';
  });

  const choose = (id: TabId) => {
    setTab(id);
    safeWrite('sabre.reference.tab', id);
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-header__title">Reference data</h1>
          <p className="page-header__subtitle">
            What the host prices, displays and sells. Changes take effect on the next entry an agent makes.
          </p>
        </div>
      </div>

      <div className="ref-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`ref-tabs__tab${tab === t.id ? ' ref-tabs__tab--active' : ''}`}
            onClick={() => choose(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'fare-rules' && <FareRulesTab key="fare-rules" />}
      {tab === 'taxes' && <TaxesTab key="taxes" />}
      {tab === 'flights' && <FlightsTab key="flights" />}
      {tab === 'itineraries' && <ItinerariesTab key="itineraries" />}
      {tab === 'airlines' && <AirlinesTab key="airlines" />}
      {tab === 'airports' && <AirportsTab key="airports" />}
      {tab === 'cities' && <CitiesTab key="cities" />}
      {tab === 'countries' && <CountriesTab key="countries" />}
      {tab === 'aircraft' && <AircraftTab key="aircraft" />}
    </>
  );
}

function safeRead(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // A blocked store only costs remembering the tab.
  }
}

function requireFields(fields: Record<string, string>, errors: Record<string, string>) {
  for (const [key, value] of Object.entries(fields)) {
    if (!value.trim()) errors[key] = 'Required.';
  }
}

function result(body: unknown, errors: Record<string, string>) {
  return Object.keys(errors).length > 0 ? { errors } : { body };
}

// ---------------------------------------------------------------- fare rules

interface FareDraft {
  carrier: string;
  origin: string;
  destination: string;
  bothWays: boolean;
  note: string;
  classes: { class: string; basis: string; fare: string }[];
}

function FareRulesTab() {
  return (
    <ResourceTab<FareRule, FareDraft>
      resource="fare-rules"
      noun="fare rule"
      searchPlaceholder="Carrier or route: BG, DAC, DACDXB"
      wide
      hint={
        <>
          A rule lists the booking classes that carry a fare for a carrier on a route. Pricing a booking in any other
          class — even one the availability display shows seats in — answers with the fares in this list for the agent
          to rebook into. Routes with no rule price on the standard ladder.
        </>
      }
      columns={[
        { header: 'Carrier', render: (r) => r.carrier, mono: true },
        { header: 'Route', render: (r) => `${r.origin}${r.bothWays ? '⇄' : '→'}${r.destination}`, mono: true },
        {
          header: 'Classes that price',
          render: (r) => (
            <span className="ref-classes">
              {r.classes.map((c, i) => (
                <span key={i} className="ref-chip" title={c.basis || undefined}>
                  {c.basis ? c.basis : c.class}
                  {c.fare ? <small> ${c.fare}</small> : null}
                </span>
              ))}
            </span>
          ),
        },
        { header: 'Note', render: (r) => r.note || '—' },
      ]}
      keyOf={(r) => String(r.id)}
      pathOf={(r) => String(r.id)}
      labelOf={(r) => `${r.carrier} ${r.origin}-${r.destination}`}
      emptyDraft={() => ({
        carrier: '',
        origin: '',
        destination: '',
        bothWays: false,
        note: '',
        classes: [{ class: '', basis: '', fare: '' }],
      })}
      toDraft={(r) => ({
        carrier: r.carrier,
        origin: r.origin,
        destination: r.destination,
        bothWays: r.bothWays,
        note: r.note,
        classes: r.classes.map((c) => ({ class: c.class, basis: c.basis ?? '', fare: c.fare ? String(c.fare) : '' })),
      })}
      toBody={(d) => {
        const errors: Record<string, string> = {};
        requireFields({ carrier: d.carrier, origin: d.origin, destination: d.destination }, errors);
        const classes: FareRuleClass[] = [];
        for (const c of d.classes) {
          if (!c.class.trim()) continue;
          const fare = c.fare.trim() === '' ? undefined : Number(c.fare);
          if (fare !== undefined && !(fare >= 0)) errors.classes = `Fare for ${c.class} is not a number.`;
          classes.push({ class: c.class.trim(), basis: c.basis.trim() || undefined, fare });
        }
        if (classes.length === 0) errors.classes = 'List at least one class.';
        return result(
          { carrier: d.carrier, origin: d.origin, destination: d.destination, bothWays: d.bothWays, note: d.note, classes },
          errors,
        );
      }}
      Form={FareRuleForm}
    />
  );
}

function FareRuleForm({ draft, set, errors, disabled }: FormProps<FareDraft>) {
  const setClass = (i: number, patch: Partial<FareDraft['classes'][number]>) =>
    set({ classes: draft.classes.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const [quick, setQuick] = useState('');

  return (
    <>
      <div className="form-grid form-grid--3">
        <AirlinePicker label="Carrier" placeholder="BG" value={draft.carrier} error={errors.carrier}
          disabled={disabled} onChange={(carrier) => set({ carrier })} />
        <AirportPicker label="From" placeholder="DAC" value={draft.origin} error={errors.origin}
          disabled={disabled} onChange={(origin) => set({ origin })} />
        <AirportPicker label="To" placeholder="DXB" value={draft.destination} error={errors.destination}
          disabled={disabled} onChange={(destination) => set({ destination })} />
      </div>
      <label className="checkbox">
        <input type="checkbox" checked={draft.bothWays} disabled={disabled}
          onChange={(e) => set({ bothWays: e.target.checked })} />
        Applies in both directions
      </label>

      <div className="ref-fares">
        <div className="ref-fares__head">
          <span>Class</span>
          <span>Fare basis (optional)</span>
          <span>Fare USD (optional)</span>
          <span />
        </div>
        {draft.classes.map((c, i) => (
          <div className="ref-fares__row" key={i}>
            <input className="ref-cell mono" maxLength={1} placeholder="K" value={c.class} disabled={disabled}
              aria-label={`Class ${i + 1}`} onChange={(e) => setClass(i, { class: up(e.target.value) })} />
            <input className="ref-cell mono" maxLength={15} placeholder={c.class ? `${c.class}BDO` : 'KBDO'}
              value={c.basis} disabled={disabled} aria-label={`Fare basis ${i + 1}`}
              onChange={(e) => setClass(i, { basis: up(e.target.value) })} />
            <input className="ref-cell mono" inputMode="decimal" placeholder="ladder" value={c.fare}
              disabled={disabled} aria-label={`Fare ${i + 1}`} onChange={(e) => setClass(i, { fare: e.target.value })} />
            <button type="button" className="spark-btn-link" disabled={disabled || draft.classes.length === 1}
              onClick={() => set({ classes: draft.classes.filter((_, j) => j !== i) })}>
              Remove
            </button>
          </div>
        ))}
        {errors.classes && <span className="spark-input__error">{errors.classes}</span>}
        <div className="ref-fares__add">
          <button type="button" className="spark-btn-link" disabled={disabled}
            onClick={() => set({ classes: [...draft.classes, { class: '', basis: '', fare: '' }] })}>
            Add a class
          </button>
          <span className="ref-fares__quick">
            or add several:
            <input className="ref-cell mono" placeholder="KBHLNAYDCJ" value={quick} disabled={disabled}
              aria-label="Classes to add" onChange={(e) => setQuick(up(e.target.value).replace(/[^A-Z]/g, ''))} />
            <button type="button" className="spark-btn-link" disabled={disabled || !quick}
              onClick={() => {
                const kept = draft.classes.filter((c) => c.class);
                set({ classes: [...kept, ...quick.split('').map((code) => ({ class: code, basis: '', fare: '' }))] });
                setQuick('');
              }}>
              Add
            </button>
          </span>
        </div>
      </div>

      <SparkInput label="Note" maxLength={200} value={draft.note} error={errors.note} disabled={disabled}
        onChange={(e) => set({ note: e.target.value })} />
    </>
  );
}

// ---------------------------------------------------------------- taxes

interface TaxDraft {
  code: string;
  name: string;
  perSector: boolean;
  origin: string;
  destination: string;
  carrier: string;
  cabin: TaxRule['cabin'];
  amount: string;
  stepKm: string;
  stepAmount: string;
  minimum: string;
  currency: string;
  position: string;
}

const CABINS: { value: TaxRule['cabin']; label: string }[] = [
  { value: '', label: 'Any cabin' },
  { value: 'economy', label: 'Economy' },
  { value: 'premium', label: 'Premium economy' },
  { value: 'business', label: 'Business' },
  { value: 'first', label: 'First' },
];

/** "USD20", "BDT0 (exempt)", "USD1 per 520 km, min USD3". */
function describeCharge(t: TaxRule): string {
  const parts: string[] = [];
  if (t.amount > 0 || t.stepAmount === 0) parts.push(`${t.currency}${t.amount}`);
  if (t.stepAmount > 0) parts.push(`${t.currency}${t.stepAmount} per ${t.stepKm} km`);
  let text = parts.join(' + ');
  if (t.minimum > 0) text += `, min ${t.currency}${t.minimum}`;
  if (t.amount === 0 && t.stepAmount === 0 && t.minimum === 0) text += ' (exempt)';
  return text;
}

function describeWhere(t: TaxRule): string {
  const parts = [`${t.origin || 'any'} → ${t.destination || 'any'}`];
  if (t.carrier) parts.push(t.carrier);
  if (t.cabin) parts.push(t.cabin);
  return parts.join(' · ');
}

function TaxesTab() {
  return (
    <ResourceTab<TaxRule, TaxDraft>
      resource="taxes"
      noun="tax"
      searchPlaceholder="Code, name, country or carrier: UT, YQ, BD"
      wide
      hint={
        <>
          The taxes, fees and surcharges added to every priced fare. A tax is charged once per ticket or on every
          sector, wherever its departure country, arrival country, carrier and cabin match (blank matches anything).
          When several rows share a code, only the most specific match is charged — so a row with no amount exempts a
          country from a tax filed for everywhere else. Amounts are converted into the currency the fare is sold in.
        </>
      }
      columns={[
        { header: 'Code', render: (t) => t.code, mono: true },
        { header: 'Name', render: (t) => t.name || '—' },
        { header: 'Applies', render: (t) => (t.perSector ? 'Each sector' : 'Per ticket') },
        { header: 'Where', render: (t) => describeWhere(t), mono: true },
        { header: 'Charge', render: (t) => describeCharge(t), mono: true },
        { header: 'Order', render: (t) => t.position, mono: true },
      ]}
      keyOf={(t) => String(t.id)}
      pathOf={(t) => String(t.id)}
      labelOf={(t) => (t.origin ? `${t.code} from ${t.origin}` : t.code)}
      emptyDraft={() => ({
        code: '',
        name: '',
        perSector: false,
        origin: '',
        destination: '',
        carrier: '',
        cabin: '',
        amount: '',
        stepKm: '',
        stepAmount: '',
        minimum: '',
        currency: 'BDT',
        position: '100',
      })}
      toDraft={(t) => ({
        code: t.code,
        name: t.name,
        perSector: t.perSector,
        origin: t.origin,
        destination: t.destination,
        carrier: t.carrier,
        cabin: t.cabin,
        amount: String(t.amount),
        stepKm: t.stepKm ? String(t.stepKm) : '',
        stepAmount: t.stepAmount ? String(t.stepAmount) : '',
        minimum: t.minimum ? String(t.minimum) : '',
        currency: t.currency,
        position: String(t.position),
      })}
      toBody={(d) => {
        const errors: Record<string, string> = {};
        requireFields({ code: d.code, currency: d.currency }, errors);
        const num = (key: keyof TaxDraft, value: string) => {
          if (value.trim() === '') return 0;
          const n = Number(value);
          if (!Number.isFinite(n) || n < 0) errors[key] = 'A number, zero or more.';
          return n;
        };
        const position = Number(d.position || 0);
        if (!Number.isInteger(position)) errors.position = 'A whole number.';
        return result(
          {
            code: d.code,
            name: d.name,
            perSector: d.perSector,
            origin: d.origin,
            destination: d.destination,
            carrier: d.carrier,
            cabin: d.cabin,
            amount: num('amount', d.amount),
            stepKm: num('stepKm', d.stepKm),
            stepAmount: num('stepAmount', d.stepAmount),
            minimum: num('minimum', d.minimum),
            currency: d.currency,
            position,
          },
          errors,
        );
      }}
      Form={TaxForm}
    />
  );
}

function TaxForm({ draft, set, errors, disabled }: FormProps<TaxDraft>) {
  return (
    <>
      <div className="form-grid form-grid--3">
        <SparkInput label="Tax code" placeholder="UT" maxLength={2} value={draft.code} error={errors.code}
          disabled={disabled} onChange={(e) => set({ code: up(e.target.value) })} />
        <SparkInput label="Name" placeholder="Travel tax" maxLength={120} value={draft.name} error={errors.name}
          disabled={disabled} onChange={(e) => set({ name: e.target.value })} />
        <label className="spark-input">
          <span className="spark-label">Charged</span>
          <select className="spark-input__field" value={draft.perSector ? 'sector' : 'ticket'} disabled={disabled}
            onChange={(e) => set({ perSector: e.target.value === 'sector' })}>
            <option value="ticket">Once per ticket</option>
            <option value="sector">On every sector</option>
          </select>
        </label>
      </div>

      <p className="ref-hint">Where it applies — leave blank for any.</p>
      <div className="form-grid form-grid--4">
        <CountryPicker label={draft.perSector ? 'Sector departs' : 'Journey departs'} placeholder="any"
          value={draft.origin} error={errors.origin} disabled={disabled} onChange={(origin) => set({ origin })} />
        <CountryPicker label={draft.perSector ? 'Sector arrives' : 'Journey lands in'} placeholder="any"
          value={draft.destination} error={errors.destination} disabled={disabled}
          onChange={(destination) => set({ destination })} />
        <AirlinePicker label={draft.perSector ? 'Operating carrier' : 'Validating carrier'} placeholder="any"
          value={draft.carrier} error={errors.carrier} disabled={disabled} onChange={(carrier) => set({ carrier })} />
        <label className={`spark-input${errors.cabin ? ' spark-input--invalid' : ''}`}>
          <span className="spark-label">Cabin</span>
          <select className="spark-input__field" value={draft.cabin} disabled={disabled}
            onChange={(e) => set({ cabin: e.target.value as TaxRule['cabin'] })}>
            {CABINS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className="ref-hint">
        How much — a fixed amount, plus an optional amount per distance step (for surcharges such as YQ), never less
        than the minimum. Leave every amount at zero to exempt.
      </p>
      <div className="form-grid form-grid--4">
        <SparkInput label="Currency" placeholder="BDT" maxLength={3} value={draft.currency} error={errors.currency}
          disabled={disabled} onChange={(e) => set({ currency: up(e.target.value) })} />
        <SparkInput label="Fixed amount" inputMode="decimal" placeholder="0" value={draft.amount}
          error={errors.amount} disabled={disabled} onChange={(e) => set({ amount: e.target.value })} />
        <SparkInput label="Plus, per step" inputMode="decimal" placeholder="0" value={draft.stepAmount}
          error={errors.stepAmount} disabled={disabled} onChange={(e) => set({ stepAmount: e.target.value })} />
        <SparkInput label="Step (km)" inputMode="decimal" placeholder="520" value={draft.stepKm}
          error={errors.stepKm} disabled={disabled} onChange={(e) => set({ stepKm: e.target.value })} />
        <SparkInput label="Minimum" inputMode="decimal" placeholder="0" value={draft.minimum}
          error={errors.minimum} disabled={disabled} onChange={(e) => set({ minimum: e.target.value })} />
        <SparkInput label="Print order" inputMode="numeric" placeholder="100" value={draft.position}
          error={errors.position} disabled={disabled} onChange={(e) => set({ position: e.target.value })} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------- flights

interface FlightDraft {
  carrier: string;
  number: string;
  origin: string;
  destination: string;
  originTerminal: string;
  destinationTerminal: string;
  depart: string;
  arrive: string;
  arriveDayOffset: string;
  aircraft: string;
  daysMask: number;
}

function FlightsTab() {
  return (
    <ResourceTab<Flight, FlightDraft>
      resource="flights"
      noun="flight"
      searchPlaceholder="Flight or route: EK583, EK, DACDXB"
      wide
      hint="The published schedule. Where a city pair has flights on file, availability shows exactly these; connections on one carrier join by themselves."
      columns={[
        { header: 'Flight', render: (f) => `${f.carrier} ${f.number}`, mono: true },
        { header: 'Route', render: (f) => `${f.origin}–${f.destination}`, mono: true },
        {
          header: 'Times',
          render: (f) =>
            `${formatMinute(f.departMinute)} → ${formatMinute(f.arriveMinute)}${
              f.arriveDayOffset ? ` ${f.arriveDayOffset > 0 ? '+' : ''}${f.arriveDayOffset}` : ''
            }`,
          mono: true,
        },
        { header: 'Days', render: (f) => formatDays(f.daysMask) },
        { header: 'Equipment', render: (f) => f.aircraft, mono: true },
      ]}
      keyOf={flightPath}
      pathOf={flightPath}
      labelOf={(f) => `${f.carrier}${f.number} ${f.origin}-${f.destination}`}
      deleteWarning="Itineraries built on it are deleted with it."
      emptyDraft={() => ({
        carrier: '', number: '', origin: '', destination: '', originTerminal: '', destinationTerminal: '',
        depart: '', arrive: '', arriveDayOffset: '0', aircraft: '', daysMask: 127,
      })}
      toDraft={(f) => ({
        ...f,
        depart: formatMinute(f.departMinute),
        arrive: formatMinute(f.arriveMinute),
        arriveDayOffset: String(f.arriveDayOffset),
      })}
      toBody={(d) => {
        const errors: Record<string, string> = {};
        requireFields({ carrier: d.carrier, number: d.number, origin: d.origin, destination: d.destination, aircraft: d.aircraft }, errors);
        const departMinute = parseMinute(d.depart);
        const arriveMinute = parseMinute(d.arrive);
        if (Number.isNaN(departMinute)) errors.departMinute = 'A time such as 09:55.';
        if (Number.isNaN(arriveMinute)) errors.arriveMinute = 'A time such as 13:20.';
        if (d.daysMask === 0) errors.daysMask = 'Pick at least one day.';
        return result(
          {
            carrier: d.carrier, number: d.number, origin: d.origin, destination: d.destination,
            originTerminal: d.originTerminal, destinationTerminal: d.destinationTerminal,
            departMinute, arriveMinute, arriveDayOffset: Number(d.arriveDayOffset),
            aircraft: d.aircraft, daysMask: d.daysMask,
          },
          errors,
        );
      }}
      Form={FlightForm}
    />
  );
}

function FlightForm({ draft, set, errors, disabled }: FormProps<FlightDraft>) {
  return (
    <>
      <div className="form-grid form-grid--4">
        <AirlinePicker label="Carrier" placeholder="EK" value={draft.carrier} error={errors.carrier}
          disabled={disabled} onChange={(carrier) => set({ carrier })} />
        <SparkInput label="Flight number" placeholder="583" maxLength={5} value={draft.number} error={errors.number}
          disabled={disabled} onChange={(e) => set({ number: up(e.target.value) })} />
        <AircraftPicker label="Equipment" placeholder="77W" value={draft.aircraft} error={errors.aircraft}
          disabled={disabled} onChange={(aircraft) => set({ aircraft })} />
        <span />
        <AirportPicker label="From" placeholder="DAC" value={draft.origin} error={errors.origin}
          disabled={disabled} onChange={(origin) => set({ origin })} />
        <SparkInput label="Terminal" placeholder="1" maxLength={5} value={draft.originTerminal}
          error={errors.originTerminal} disabled={disabled}
          onChange={(e) => set({ originTerminal: up(e.target.value) })} />
        <AirportPicker label="To" placeholder="DXB" value={draft.destination} error={errors.destination}
          disabled={disabled} onChange={(destination) => set({ destination })} />
        <SparkInput label="Terminal" placeholder="3" maxLength={5} value={draft.destinationTerminal}
          error={errors.destinationTerminal} disabled={disabled}
          onChange={(e) => set({ destinationTerminal: up(e.target.value) })} />
        <SparkInput label="Departs (local)" type="time" value={draft.depart}
          error={errors.departMinute} disabled={disabled} onChange={(e) => set({ depart: e.target.value })} />
        <SparkInput label="Arrives (local)" type="time" value={draft.arrive}
          error={errors.arriveMinute} disabled={disabled} onChange={(e) => set({ arrive: e.target.value })} />
        <label className="spark-input">
          <span className="spark-label">Arrival day</span>
          <select className="spark-input__field" value={draft.arriveDayOffset} disabled={disabled}
            onChange={(e) => set({ arriveDayOffset: e.target.value })}>
            <option value="-1">Day before</option>
            <option value="0">Same day</option>
            <option value="1">Next day (+1)</option>
            <option value="2">+2</option>
          </select>
        </label>
      </div>
      <div className="ref-days" role="group" aria-label="Operating days">
        <span className="spark-label">Operates</span>
        {WEEKDAYS.map((day, i) => (
          <label key={day} className="ref-days__day">
            <input type="checkbox" checked={(draft.daysMask & (1 << i)) !== 0} disabled={disabled}
              onChange={(e) => set({ daysMask: e.target.checked ? draft.daysMask | (1 << i) : draft.daysMask & ~(1 << i) })} />
            {day}
          </label>
        ))}
        <button type="button" className="spark-btn-link" disabled={disabled} onClick={() => set({ daysMask: 127 })}>
          Daily
        </button>
      </div>
      {errors.daysMask && <span className="spark-input__error">{errors.daysMask}</span>}
    </>
  );
}

// ---------------------------------------------------------------- itineraries

interface ItineraryDraft {
  first: string;
  firstOrigin: string;
  second: string;
  secondOrigin: string;
}

/** "BG121" → carrier BG, number 121. */
function splitFlight(value: string): { carrier: string; number: string } | null {
  const match = /^([A-Z0-9]{2})\s*(\d{1,4}[A-Z]?)$/.exec(value.trim().toUpperCase());
  return match ? { carrier: match[1], number: match[2] } : null;
}

function ItinerariesTab() {
  const leg = (l: Itinerary['first']) =>
    `${l.carrier}${l.number} ${l.origin}–${l.destination} ${formatMinute(l.departMinute)}–${formatMinute(l.arriveMinute)}`;
  return (
    <ResourceTab<Itinerary, ItineraryDraft>
      resource="itineraries"
      noun="itinerary"
      searchPlaceholder="Flight, carrier or airport: BG121, WY, MCT"
      createOnly
      hint="Two filed flights offered together as a connection. Use this for interline journeys such as BG DAC–MCT on to WY MCT–FCO, which availability only shows once they are on file."
      columns={[
        { header: 'Journey', render: (it) => `${it.first.origin}–${it.first.destination}–${it.second.destination}`, mono: true },
        { header: 'First flight', render: (it) => leg(it.first), mono: true },
        { header: 'Onward flight', render: (it) => leg(it.second), mono: true },
      ]}
      keyOf={(it) => String(it.id)}
      pathOf={(it) => String(it.id)}
      labelOf={(it) => `${it.first.carrier}${it.first.number}/${it.second.carrier}${it.second.number}`}
      emptyDraft={() => ({ first: '', firstOrigin: '', second: '', secondOrigin: '' })}
      toDraft={(it) => ({
        first: it.first.carrier + it.first.number, firstOrigin: it.first.origin,
        second: it.second.carrier + it.second.number, secondOrigin: it.second.origin,
      })}
      toBody={(d) => {
        const errors: Record<string, string> = {};
        const first = splitFlight(d.first);
        const second = splitFlight(d.second);
        if (!first) errors.first = 'A flight such as BG121.';
        if (!second) errors.second = 'A flight such as WY147.';
        requireFields({ firstOrigin: d.firstOrigin, secondOrigin: d.secondOrigin }, errors);
        return result(
          { first: { ...first, origin: d.firstOrigin }, second: { ...second, origin: d.secondOrigin } },
          errors,
        );
      }}
      Form={ItineraryForm}
    />
  );
}

/** Searches the schedule; picking a flight fills in where it leaves from. */
function FlightPicker(props: PickerProps & { onPick: (flight: Flight) => void }) {
  return (
    <CodePicker<Flight> {...props} resource="flights" maxLength={7}
      toOption={(f) => ({
        code: f.carrier + f.number,
        label: `${f.origin}–${f.destination} ${formatMinute(f.departMinute)}–${formatMinute(f.arriveMinute)}`,
      })} />
  );
}

function ItineraryForm({ draft, set, errors, disabled }: FormProps<ItineraryDraft>) {
  return (
    <>
      <p className="field-hint">
        Search by flight or route. Both flights must be on the Flights tab, and the onward flight must leave from
        where the first lands — picking the first flight fills that in.
      </p>
      <div className="form-grid">
        <FlightPicker label="First flight" placeholder="BG121" value={draft.first} error={errors.first}
          disabled={disabled} onChange={(first) => set({ first })}
          onPick={(f) => set({ first: f.carrier + f.number, firstOrigin: f.origin, secondOrigin: f.destination })} />
        <AirportPicker label="Departing from" placeholder="DAC" value={draft.firstOrigin}
          error={errors.firstOrigin} disabled={disabled} onChange={(firstOrigin) => set({ firstOrigin })} />
        <FlightPicker label="Onward flight" placeholder="WY147" value={draft.second} error={errors.second}
          disabled={disabled} onChange={(second) => set({ second })}
          onPick={(f) => set({ second: f.carrier + f.number, secondOrigin: f.origin })} />
        <AirportPicker label="Departing from" placeholder="MCT" value={draft.secondOrigin}
          error={errors.secondOrigin} disabled={disabled} onChange={(secondOrigin) => set({ secondOrigin })} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------- airlines

type AirlineDraft = Airline;

function AirlinesTab() {
  return (
    <ResourceTab<Airline, AirlineDraft>
      resource="airlines"
      noun="airline"
      searchPlaceholder="Code or name: EK, Emirates"
      hint="The booking classes are what the availability display prints for the carrier, cabin by cabin, in order."
      columns={[
        { header: 'Code', render: (a) => a.code, mono: true },
        { header: 'Name', render: (a) => a.name },
        { header: 'Country', render: (a) => a.country || '—', mono: true },
        {
          header: 'Booking classes',
          render: (a) =>
            [a.classes.first, a.classes.business, a.classes.premium, a.classes.economy].filter(Boolean).join(' · ') || '—',
          mono: true,
        },
      ]}
      keyOf={(a) => a.code}
      pathOf={(a) => a.code}
      labelOf={(a) => a.code}
      deleteWarning="Its flights and fare rules are deleted with it."
      emptyDraft={() => ({ code: '', name: '', country: '', classes: { first: '', business: '', premium: '', economy: '' } })}
      toDraft={(a) => a}
      toBody={(d) => {
        const errors: Record<string, string> = {};
        requireFields({ code: d.code, name: d.name }, errors);
        return result(d, errors);
      }}
      Form={AirlineForm}
    />
  );
}

function AirlineForm({ draft, set, errors, editing, disabled }: FormProps<AirlineDraft>) {
  const setClasses = (patch: Partial<Airline['classes']>) =>
    set({ classes: { ...draft.classes, ...patch } });
  const cabin = (key: keyof Airline['classes'], label: string, placeholder: string) => (
    <SparkInput label={label} placeholder={placeholder} maxLength={26} value={draft.classes[key]}
      error={errors[`classes.${key}`]} disabled={disabled}
      onChange={(e) => setClasses({ [key]: up(e.target.value).replace(/[^A-Z]/g, '') })} />
  );
  return (
    <div className="form-grid">
      <SparkInput label="Code" placeholder="EK" maxLength={2} value={draft.code} error={errors.code}
        disabled={disabled || editing} onChange={(e) => set({ code: up(e.target.value) })} />
      <CountryPicker label="Country" placeholder="AE" value={draft.country} error={errors.country}
        disabled={disabled} onChange={(country) => set({ country })} />
      <div className="form-grid--full">
        <SparkInput label="Name" placeholder="EMIRATES" value={draft.name} error={errors.name} disabled={disabled}
          onChange={(e) => set({ name: e.target.value })} />
      </div>
      {cabin('first', 'First classes', 'FA')}
      {cabin('business', 'Business classes', 'JCDIO')}
      {cabin('premium', 'Premium economy classes', 'W')}
      {cabin('economy', 'Economy classes', 'YRBMUKQLTV')}
    </div>
  );
}

// ---------------------------------------------------------------- airports

interface AirportDraft {
  code: string;
  name: string;
  city: string;
  country: string;
  timezone: string;
  latitude: string;
  longitude: string;
}

function AirportsTab() {
  return (
    <ResourceTab<Airport, AirportDraft>
      resource="airports"
      noun="airport"
      searchPlaceholder="Code, name, city or country: DAC, Dhaka, BD"
      hint="Airports with coordinates are listed first. Coordinates give distances for pricing; a filed flight only needs the time zone."
      columns={[
        { header: 'Code', render: (a) => a.code, mono: true },
        { header: 'Name', render: (a) => a.name },
        { header: 'City', render: (a) => a.city },
        { header: 'Country', render: (a) => a.country, mono: true },
        { header: 'Time zone', render: (a) => a.timezone },
        {
          header: 'Coordinates',
          render: (a) => (a.latitude == null ? '—' : `${a.latitude.toFixed(3)}, ${a.longitude?.toFixed(3)}`),
          mono: true,
        },
      ]}
      keyOf={(a) => a.code}
      pathOf={(a) => a.code}
      labelOf={(a) => a.code}
      deleteWarning="Flights and fare rules that use it are deleted with it."
      emptyDraft={() => ({ code: '', name: '', city: '', country: '', timezone: '', latitude: '', longitude: '' })}
      toDraft={(a) => ({
        ...a,
        latitude: a.latitude == null ? '' : String(a.latitude),
        longitude: a.longitude == null ? '' : String(a.longitude),
      })}
      toBody={(d) => {
        const errors: Record<string, string> = {};
        requireFields({ code: d.code, name: d.name, city: d.city, country: d.country, timezone: d.timezone }, errors);
        const coord = (value: string, field: string) => {
          if (value.trim() === '') return null;
          const n = Number(value);
          if (Number.isNaN(n)) errors[field] = 'A number in decimal degrees.';
          return n;
        };
        return result({ ...d, latitude: coord(d.latitude, 'latitude'), longitude: coord(d.longitude, 'longitude') }, errors);
      }}
      Form={AirportForm}
    />
  );
}

function AirportForm({ draft, set, errors, editing, disabled }: FormProps<AirportDraft>) {
  return (
    <div className="form-grid">
      <SparkInput label="Code" placeholder="DAC" maxLength={3} value={draft.code} error={errors.code}
        disabled={disabled || editing} onChange={(e) => set({ code: up(e.target.value) })} />
      <CountryPicker label="Country" placeholder="BD" value={draft.country} error={errors.country}
        disabled={disabled} onChange={(country) => set({ country })} />
      <div className="form-grid--full">
        <SparkInput label="Name" placeholder="HAZRAT SHAHJALAL INTL" value={draft.name} error={errors.name}
          disabled={disabled} onChange={(e) => set({ name: e.target.value })} />
      </div>
      <SparkInput label="City" placeholder="DHAKA" value={draft.city} error={errors.city} disabled={disabled}
        onChange={(e) => set({ city: e.target.value })} />
      <SparkInput label="Time zone" placeholder="Asia/Dhaka" value={draft.timezone} error={errors.timezone}
        disabled={disabled} onChange={(e) => set({ timezone: e.target.value })} />
      <SparkInput label="Latitude" placeholder="23.843" value={draft.latitude} error={errors.latitude}
        disabled={disabled} onChange={(e) => set({ latitude: e.target.value })} />
      <SparkInput label="Longitude" placeholder="90.398" value={draft.longitude} error={errors.longitude}
        disabled={disabled} onChange={(e) => set({ longitude: e.target.value })} />
    </div>
  );
}

// ---------------------------------------------------------------- cities

interface CityDraft {
  code: string;
  airports: string;
}

function CitiesTab() {
  return (
    <ResourceTab<City, CityDraft>
      resource="cities"
      noun="city"
      searchPlaceholder="City or airport code: LON, LHR"
      hint="Metropolitan codes: an availability search for LON covers every airport listed, the first being the principal one."
      columns={[
        { header: 'City code', render: (c) => c.code, mono: true },
        { header: 'Airports', render: (c) => c.airports.join(' '), mono: true },
      ]}
      keyOf={(c) => c.code}
      pathOf={(c) => c.code}
      labelOf={(c) => c.code}
      emptyDraft={() => ({ code: '', airports: '' })}
      toDraft={(c) => ({ code: c.code, airports: c.airports.join(' ') })}
      toBody={(d) => {
        const errors: Record<string, string> = {};
        requireFields({ code: d.code, airports: d.airports }, errors);
        return result({ code: d.code, airports: d.airports.split(/[\s,]+/).filter(Boolean) }, errors);
      }}
      Form={({ draft, set, errors, editing, disabled }: FormProps<CityDraft>) => (
        <>
          <SparkInput label="City code" placeholder="LON" maxLength={3} value={draft.code} error={errors.code}
            disabled={disabled || editing} onChange={(e) => set({ code: up(e.target.value) })} />
          <SparkInput label="Airports, principal first" placeholder="LHR LGW STN LTN LCY" value={draft.airports}
            error={errors.airports} disabled={disabled} onChange={(e) => set({ airports: up(e.target.value) })} />
        </>
      )}
    />
  );
}

// ---------------------------------------------------------------- countries

function CountriesTab() {
  return (
    <ResourceTab<Country, Country>
      resource="countries"
      noun="country"
      searchPlaceholder="Code or name: BD, Bangladesh"
      columns={[
        { header: 'Code', render: (c) => c.code, mono: true },
        { header: 'Name', render: (c) => c.name },
      ]}
      keyOf={(c) => c.code}
      pathOf={(c) => c.code}
      labelOf={(c) => c.code}
      emptyDraft={() => ({ code: '', name: '' })}
      toDraft={(c) => c}
      toBody={(d) => {
        const errors: Record<string, string> = {};
        requireFields({ code: d.code, name: d.name }, errors);
        return result(d, errors);
      }}
      Form={({ draft, set, errors, editing, disabled }: FormProps<Country>) => (
        <div className="form-grid">
          <SparkInput label="Code" placeholder="BD" maxLength={2} value={draft.code} error={errors.code}
            disabled={disabled || editing} onChange={(e) => set({ code: up(e.target.value) })} />
          <SparkInput label="Name" placeholder="BANGLADESH" value={draft.name} error={errors.name}
            disabled={disabled} onChange={(e) => set({ name: e.target.value })} />
        </div>
      )}
    />
  );
}

// ---------------------------------------------------------------- aircraft

interface AircraftDraft {
  code: string;
  name: string;
  maxKm: string;
  wide: boolean;
  hasFirst: boolean;
}

function AircraftTab() {
  return (
    <ResourceTab<Aircraft, AircraftDraft>
      resource="aircraft"
      noun="aircraft"
      searchPlaceholder="Code or name: 77W, 777"
      columns={[
        { header: 'Code', render: (a) => a.code, mono: true },
        { header: 'Name', render: (a) => a.name },
        { header: 'Range', render: (a) => `${a.maxKm.toLocaleString()} km` },
        { header: 'Cabins', render: (a) => [a.hasFirst && 'First', a.wide ? 'Widebody' : 'Narrowbody'].filter(Boolean).join(' · ') },
      ]}
      keyOf={(a) => a.code}
      pathOf={(a) => a.code}
      labelOf={(a) => a.code}
      deleteWarning="Flights flown by it are deleted with it."
      emptyDraft={() => ({ code: '', name: '', maxKm: '', wide: false, hasFirst: false })}
      toDraft={(a) => ({ ...a, maxKm: String(a.maxKm) })}
      toBody={(d) => {
        const errors: Record<string, string> = {};
        requireFields({ code: d.code, name: d.name, maxKm: d.maxKm }, errors);
        const maxKm = Number(d.maxKm);
        if (d.maxKm && !Number.isInteger(maxKm)) errors.maxKm = 'Whole kilometres.';
        return result({ ...d, maxKm }, errors);
      }}
      Form={({ draft, set, errors, editing, disabled }: FormProps<AircraftDraft>) => (
        <>
          <div className="form-grid">
            <SparkInput label="Code" placeholder="77W" maxLength={3} value={draft.code} error={errors.code}
              disabled={disabled || editing} onChange={(e) => set({ code: up(e.target.value) })} />
            <SparkInput label="Range (km)" placeholder="13650" value={draft.maxKm} error={errors.maxKm}
              disabled={disabled} onChange={(e) => set({ maxKm: e.target.value.replace(/\D/g, '') })} />
            <div className="form-grid--full">
              <SparkInput label="Name" placeholder="BOEING 777-300ER" value={draft.name} error={errors.name}
                disabled={disabled} onChange={(e) => set({ name: e.target.value })} />
            </div>
          </div>
          <label className="checkbox">
            <input type="checkbox" checked={draft.wide} disabled={disabled} onChange={(e) => set({ wide: e.target.checked })} />
            Widebody (shows premium economy)
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={draft.hasFirst} disabled={disabled}
              onChange={(e) => set({ hasFirst: e.target.checked })} />
            Fitted with a first class cabin
          </label>
        </>
      )}
    />
  );
}
